// The live-session status vocabulary, kept in its own module rather than in
// `useWsSession` on purpose: the surfaces' tests mock the whole `useWsSession`
// module, so a predicate exported from there would vanish under every such
// mock. Importing it from here keeps it real everywhere.

/**
 * Where a session's socket stands.
 *
 * - `connecting` / `open` / `closed` — the ordinary lifecycle. `closed` is a
 *   real drop we auto-reconnect from (Cloud Run 1006 and friends).
 * - `expired` — the server says this session is GONE. Recovery: start a new one.
 * - `denied` — the server refused this IDENTITY (approval revoked, or a session
 *   URL opened by another user). Terminal, and a NEW session is refused the
 *   same way, so "start over" is not a recovery.
 * - `idle_killed` — we hard-DELETEd the session after a stretch of human
 *   inactivity with `?reason=idle`: the CI job is cancelled and the sandbox is
 *   gone, but `endedAt` is never stamped, so the SESSION is still there and
 *   `POST /sessions/:id/reconnect` resumes it in place.
 */
export type SessionStatus =
  | "connecting"
  | "open"
  | "closed"
  | "expired"
  | "denied"
  | "idle_killed";

/**
 * Is this session unusable with no reconnect that can fix it? True for both
 * terminal server refusals — `expired` (gone) and `denied` (refused). The
 * surfaces' self-heal (dropping the persisted per-project session binding) and
 * their fatal screens key on THIS, not on `expired` alone: a denial handled
 * only by the expiry branch would keep re-adopting a binding it can never open
 * again, which is the reconnect loop wearing a different hat.
 */
export const isSessionDead = (status: SessionStatus): boolean =>
  status === "expired" || status === "denied";
